import { apiClient } from '@api';
import { useApplicationStore } from '@data';
import {
  Provider,
  RebookingAvailabilities,
  RescheduledOrNewReservedVisit,
  TimeSlot,
} from '@types';
import { isAxiosError } from 'axios';

export async function assignReservation(reservationId: string): Promise<void> {
  await apiClient.put(`/api/v1/booking/reservations/${reservationId}/assign`);
}
interface FetchRebookingAvailabilitiesParams {
  date?: string | null;
  timeZone: string;
  visitTypeId: string;
}

interface ReserveVisitAuthedParams {
  dateTime: string;
  providerId: string;
  timeZone: string;
  visitTypeId: string;
}

export async function fetchRebookingAvailabilities({
  date,
  timeZone,
  visitTypeId,
}: FetchRebookingAvailabilitiesParams): Promise<RebookingAvailabilities> {
  const { flags } = useApplicationStore.getState();
  const isPatientVisibilityOverrideDisabled =
    (!flags.useProviderVisibility).toString();
  const queryParams = new URLSearchParams(
    date
      ? {
          date,
          timeZone,
          visitTypeId,
          isPatientVisibilityOverrideDisabled,
        }
      : { timeZone, visitTypeId, isPatientVisibilityOverrideDisabled },
  ).toString();
  const { data } = await apiClient.get<RebookingAvailabilities>(
    `/api/v1/booking/availability/rebooking?${queryParams}`,
  );

  return data;
}

export async function reserveNewVisitAvailableTime(
  params: ReserveVisitAuthedParams,
  type: string,
) {
  try {
    const { data } = await apiClient.post<RescheduledOrNewReservedVisit>(
      `/api/v1/booking/reservations/${type}`,
      params,
    );

    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw error;
    }

    console.error('An unexpected error occurred', error);
    throw new Error('An unexpected error occurred');
  }
}

export async function cancelReservationAuthed(reservationId: string) {
  await apiClient.delete(`/api/v1/booking/reservations/${reservationId}`);
}

interface ProviderAvailabilityResponse {
  hasPastVisit: boolean;
  provider: Provider;
  timeSlot: TimeSlot;
}

export async function fetchProvidersAuthed(
  visitTypeId: string,
  timeZone: string,
): Promise<Provider[]> {
  const { flags } = useApplicationStore.getState();
  const isPatientVisibilityOverrideDisabled =
    (!flags.useProviderVisibility).toString();
  const queryParams = new URLSearchParams({
    visitTypeId,
    timeZone,
    isPatientVisibilityOverrideDisabled,
  }).toString();

  // This endpoint properly handles PRISM and visit visibility settings to show providers who are configured to only be available for repeat patients
  const { data } = await apiClient.get<ProviderAvailabilityResponse[]>(
    `/api/v1/booking/availability/search?${queryParams}`,
  );

  // we are only using this for provider search, so we drop the non-provider fields
  return data.map((item) => item.provider);
}
